import React, { useState, useEffect } from 'react';
import { bookingsAPI, publicAPI, productsAPI } from './services/api';
import PaymentModal from './PaymentModal';
import SalesAgreement from './SalesAgreement';
import { validatePhone, normalizePhone, formatPhoneDisplay } from './utils/phoneUtils';
import './BreakfastBooking.css';

interface AgeGroup {
  adults: number;
  children7_12: number;
  children0_6: number;
}

interface PricingConfig {
  sunbed: { price: number; total: number; rows: number; perRow: number };
  breakfast: { adultPrice: number; child4_6Price: number; child7_12Price: number };
  dinner: { adultPrice: number; child4_6Price: number; child7_12Price: number };
}

const BreakfastBooking: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [blockedDates, setBlockedDates] = useState<{ date: string; reason: string; affects: string[] }[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup>({
    adults: 1,
    children7_12: 0,
    children0_6: 0
  });
  const [menuHTML, setMenuHTML] = useState<string>('');
  const [pricing, setPricing] = useState<PricingConfig['breakfast']>({
    adultPrice: 0,
    child4_6Price: 0,
    child7_12Price: 0
  });
  const [pricingLoaded, setPricingLoaded] = useState(false);
  
  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  
  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  // Capacity state
  const [capacityInfo, setCapacityInfo] = useState<{
    maxCapacity: number;
    currentReserved: number;
    remainingCapacity: number;
    isFull: boolean;
  } | null>(null);

  useEffect(() => {
    // Backend'den günübirlik kahvaltı fiyat ve menüsünü çek
    productsAPI.getByType('breakfast').then(res => {
      if (res.data?.data?.content) {
        const content = res.data.data.content;
        if (content.price_rules) {
          setPricing({
            adultPrice: content.price_rules.adult ?? 0,
            child4_6Price: content.price_rules.child_0_6 ?? 0,
            child7_12Price: content.price_rules.child_7_12 ?? 0
          });
        }
        if (content.menu_html) {
          setMenuHTML(content.menu_html);
        }
      }
      setPricingLoaded(true);
    }).catch(e => {
      console.error('Kahvaltı fiyatları yüklenemedi:', e);
      setPricingLoaded(true);
    });
  }, []);

  // Seçilen tarih değiştiğinde kapasiteyi kontrol et
  useEffect(() => {
    if (selectedDate && isWeekend(selectedDate)) {
      fetchCapacity(selectedDate);
    } else {
      setCapacityInfo(null);
    }
  }, [selectedDate]);

  // Kapalı tarihleri yükle
  useEffect(() => {
    publicAPI.getBlockedDates()
      .then(res => { if (res.data.success) setBlockedDates(res.data.data); })
      .catch(() => {});
  }, []);

  const isDateBlocked = (dateStr: string): boolean => {
    return blockedDates.some(entry =>
      entry.date === dateStr &&
      (entry.affects.includes('all') || entry.affects.includes('breakfast'))
    );
  };

  const getBlockedReason = (dateStr: string): string => {
    const entry = blockedDates.find(e =>
      e.date === dateStr &&
      (e.affects.includes('all') || e.affects.includes('breakfast'))
    );
    return entry?.reason || '';
  };

  const fetchCapacity = async (date: string) => {
    try {
      const response = await fetch(`/api/products/breakfast/capacity/${date}`);
      const data = await response.json();
      if (data.success) {
        setCapacityInfo(data.data);
      }
    } catch (error) {
      console.error('Kapasite bilgisi alınamadı:', error);
    }
  };

  const isWeekend = (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Pazar, 6 = Cumartesi
  };

  const calculateTotal = (): number => {
    const adultTotal = ageGroups.adults * pricing.adultPrice;
    const child7_12Total = ageGroups.children7_12 * pricing.child7_12Price;
    const child0_6Total = 0; // 0-6 yaş ücretsiz
    return adultTotal + child7_12Total + child0_6Total;
  };

  const getTotalPersons = (): number => {
    return ageGroups.adults + ageGroups.children7_12 + ageGroups.children0_6;
  };

  const handleAgeGroupChange = (type: keyof AgeGroup, value: number) => {
    setAgeGroups(prev => ({
      ...prev,
      [type]: Math.max(0, value)
    }));
  };

  const handleBooking = async () => {
    if (!selectedDate) {
      alert('Lütfen bir tarih seçin!');
      return;
    }

    if (!isWeekend(selectedDate)) {
      alert('Kahvaltı servisi sadece hafta sonları (Cumartesi-Pazar) mevcuttur!');
      return;
    }

    if (isDateBlocked(selectedDate)) {
      const reason = getBlockedReason(selectedDate);
      alert(`Bu tarihte kahvaltı servisi verilememektedir${reason ? ` (${reason})` : ''}.`);
      return;
    }

    if (getTotalPersons() === 0) {
      alert('Lütfen en az bir kişi seçin!');
      return;
    }

    if (!customerName.trim()) {
      alert('Lütfen adınızı ve soyadınızı girin!');
      return;
    }

    if (!customerEmail.trim()) {
      alert('Lütfen e-posta adresinizi girin!');
      return;
    }

    const phoneValidation = validatePhone(customerPhone);
    if (!phoneValidation.valid) {
      alert(`Telefon numarası hatalı: ${phoneValidation.error}`);
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        product_type: 'breakfast',
        booking_date: selectedDate,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: phoneValidation.formatted,
        customer_info: {
          adults: ageGroups.adults,
          children7_12: ageGroups.children7_12,
          children0_6: ageGroups.children0_6
        },
        items: {
          adults: ageGroups.adults,
          children7_12: ageGroups.children7_12,
          children0_6: ageGroups.children0_6,
          totalPersons: getTotalPersons(),
          pricing: pricing
        },
        total_price: calculateTotal(),
        notes: ''
      };

      const response = await bookingsAPI.create(bookingData);

      if (response.data.success) {
        const { iframeUrl, reservationId } = response.data.data;
        setPaymentUrl(iframeUrl);
        setCreatedBookingId(reservationId);
        setShowPaymentModal(true);
      }
    } catch (err: any) {
      console.error('Rezervasyon hatası:', err);
      alert('Rezervasyon oluşturulamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getWeekendDates = (): string[] => {
    const dates: string[] = [];
    const today = new Date();
    
    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const day = date.getDay();
      if (day === 0 || day === 6) {
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    
    return dates;
  };

  const weekendDates = getWeekendDates().filter(d => !isDateBlocked(d));

  return (
    <div className="breakfast-booking">
      <div className="breakfast-container">
        {/* Sol Panel - Bilgilendirme */}
        <div className="info-panel">
          <div className="info-card">
            <h2>🕐 Servis Saatleri</h2>
            <p><strong>Cumartesi & Pazar</strong></p>
            <p>09:30 - 12:00</p>
          </div>

          <div className="info-card">
            <h2>💰 Fiyat Listesi</h2>
            <div className="price-list">
              <div className="price-item">
                <span>Yetişkin (13+ yaş)</span>
                <span className="price">₺{pricing.adultPrice}</span>
              </div>
              <div className="price-item">
                <span>Çocuk (7-12 yaş)</span>
                <span className="price discount">₺{pricing.child7_12Price}</span>
              </div>
              <div className="price-item">
                <span>Çocuk (4-6 yaş)</span>
                <span className="price">₺{pricing.child4_6Price}</span>
              </div>
              <div className="price-item">
                <span>Çocuk (0-3 yaş)</span>
                <span className="price free">Ücretsiz</span>
              </div>
            </div>
          </div>

          <div className="info-card menu-card">
            <h2>🍽️ Kahvaltı Menüsü</h2>
            {menuHTML ? (
              <div 
                className="menu-preview-content" 
                dangerouslySetInnerHTML={{ __html: menuHTML }}
              />
            ) : (
              <ul>
                <li>🧀 Kahvaltılık peynir çeşitleri</li>
                <li>🥖 Taze fırın ürünleri</li>
                <li>🍳 Sıcak yemekler</li>
                <li>🥗 Taze sebze ve meyve</li>
                <li>☕ Sınırsız çay & kahve</li>
                <li>🥞 Pancake & Waffle</li>
                <li>🍯 Organik bal & reçel</li>
                <li>🥤 Taze sıkılmış meyve suları</li>
              </ul>
            )}
          </div>
        </div>

        {/* Sağ Panel - Rezervasyon Formu */}
        <div className="booking-panel">
          <div className="booking-card">
            <h2>Rezervasyon Yap</h2>
            
            {/* Tarih Seçimi */}
            <div className="form-group">
              <label>📅 Tarih Seçin (Sadece Hafta Sonu)</label>
              <select 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-select"
              >
                <option value="">Tarih seçiniz...</option>
                {weekendDates.map(date => {
                  const dateObj = new Date(date);
                  const formatted = dateObj.toLocaleDateString('tr-TR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long',
                    year: 'numeric'
                  });
                  return (
                    <option key={date} value={date}>
                      {formatted}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Kapasite Uyarısı */}
            {capacityInfo && selectedDate && (
              <div className={`capacity-info ${capacityInfo.isFull ? 'full' : capacityInfo.remainingCapacity < 10 ? 'warning' : 'available'}`}>
                {capacityInfo.isFull ? (
                  <span>❌ Bu tarih için kapasite dolmuştur</span>
                ) : (
                  <span>
                    ✅ Kalan kapasite: {capacityInfo.remainingCapacity} kişi 
                    ({capacityInfo.currentReserved}/{capacityInfo.maxCapacity})
                  </span>
                )}
              </div>
            )}

            {/* Kişi Sayısı Seçimi */}
            <div className="form-group">
              <label>👥 Kişi Sayısı</label>
              
              <div className="person-counter">
                <div className="counter-item">
                  <div className="counter-label">
                    <span className="label-title">Yetişkin</span>
                    <span className="label-subtitle">(13+ yaş) - ₺{pricing.adultPrice}</span>
                  </div>
                  <div className="counter-controls">
                    <button 
                      onClick={() => handleAgeGroupChange('adults', ageGroups.adults - 1)}
                      disabled={ageGroups.adults === 0}
                    >
                      -
                    </button>
                    <span className="counter-value">{ageGroups.adults}</span>
                    <button onClick={() => handleAgeGroupChange('adults', ageGroups.adults + 1)}>
                      +
                    </button>
                  </div>
                </div>

                <div className="counter-item">
                  <div className="counter-label">
                    <span className="label-title">Çocuk (7-12 yaş)</span>
                    <span className="label-subtitle">₺{pricing.child7_12Price}</span>
                  </div>
                  <div className="counter-controls">
                    <button 
                      onClick={() => handleAgeGroupChange('children7_12', ageGroups.children7_12 - 1)}
                      disabled={ageGroups.children7_12 === 0}
                    >
                      -
                    </button>
                    <span className="counter-value">{ageGroups.children7_12}</span>
                    <button onClick={() => handleAgeGroupChange('children7_12', ageGroups.children7_12 + 1)}>
                      +
                    </button>
                  </div>
                </div>

                <div className="counter-item">
                  <div className="counter-label">
                    <span className="label-title">Çocuk (0-6 yaş)</span>
                    <span className="label-subtitle free-label">Ücretsiz</span>
                  </div>
                  <div className="counter-controls">
                    <button 
                      onClick={() => handleAgeGroupChange('children0_6', ageGroups.children0_6 - 1)}
                      disabled={ageGroups.children0_6 === 0}
                    >
                      -
                    </button>
                    <span className="counter-value">{ageGroups.children0_6}</span>
                    <button onClick={() => handleAgeGroupChange('children0_6', ageGroups.children0_6 + 1)}>
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Özet */}
            <div className="booking-summary-box">
              <div className="summary-row">
                <span>Toplam Kişi:</span>
                <span className="summary-value">{getTotalPersons()} kişi</span>
              </div>
              <div className="summary-row total-row">
                <span>Toplam Ücret:</span>
                <span className="summary-price">₺{calculateTotal().toLocaleString()}</span>
              </div>
            </div>

            {/* Müşteri Bilgileri */}
            {getTotalPersons() > 0 && selectedDate && isWeekend(selectedDate) && (
              <div className="customer-info-form" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#2c3e50' }}>👤 İletişim Bilgileri</h3>
                
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Ad Soyad *</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Adınız ve Soyadınız"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>E-posta Adresi *</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Telefon Numarası *</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value.replace(/[^0-9+\s]/g, ''))}
                    onBlur={() => { const v = validatePhone(customerPhone); if (v.valid) setCustomerPhone(formatPhoneDisplay(customerPhone)); }}
                    placeholder="05XX XXX XX XX"
                    maxLength={20}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                    required
                  />
                  {customerPhone && !validatePhone(customerPhone).valid && (
                    <small style={{ color: '#e74c3c', marginTop: '4px', display: 'block' }}>⚠️ {validatePhone(customerPhone).error}</small>
                  )}
                </div>
              </div>
            )}

            {/* Satış Sözleşmesi */}
            <div style={{ marginTop: '1.5rem' }}>
              <SalesAgreement
                isChecked={agreementAccepted}
                onCheckChange={setAgreementAccepted}
              />
            </div>

            <button 
              className="btn-breakfast-book" 
              onClick={handleBooking}
              disabled={loading || !selectedDate || getTotalPersons() === 0 || !customerName.trim() || !customerEmail.trim() || !customerPhone.trim() || !agreementAccepted}
            >
              {loading ? '⏳ İşleniyor...' : '💳 Ödeme Sayfasına Geç'}
            </button>

            {!isWeekend(selectedDate) && selectedDate && (
              <div className="warning-message">
                ⚠️ Lütfen bir hafta sonu günü seçin (Cumartesi veya Pazar)
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        iframeUrl={paymentUrl}
        bookingId={createdBookingId}
        skipAgreement={true}
      />
    </div>
  );
};

export default BreakfastBooking;
